import type { IEmailCampaignDesignStatus } from "api/rpc/2024-04/facilityAdmin/marketing/emailBuilderTypes";
import type { TBadgeType } from "components/badge/Badge";
import startCase from "lodash/startCase";

export type TEmailCampaignDesignDisplayStatus = "not_designed" | "unpublished_changes" | "current";
export type TEmailCampaignDesignStepBadgeType = "success" | "warning" | "gray";
export type TEmailCampaignFinalPublishDecision =
  | "allowed"
  | "design_required"
  | "published_design_required";

export type TEmailCampaignStatus = "published" | "pending" | "scheduled" | "draft" | "processing";

export function emailCampaignBadgeType(status: string | null | undefined): TBadgeType {
  if (status === "published") {
    return "success";
  }

  if (status === "pending" || status === "scheduled" || status === "processing") {
    return "warning";
  }

  return "gray";
}

export function emailCampaignBadgeLabel(status: string | null | undefined): string {
  return startCase(status ?? "draft");
}

export function canUnpublishEmailCampaign(status: string | null | undefined): boolean {
  return status === "scheduled";
}

export function emailCampaignDesignHasUnpublishedChanges(
  design: IEmailCampaignDesignStatus | null | undefined,
  draftRevision?: number | null,
): boolean {
  if (design == null || design.active_published_design_id == null) {
    return false;
  }

  if (design.has_unpublished_changes) {
    return true;
  }

  const revision = draftRevision ?? design.draft_revision;
  if (revision == null) {
    return false;
  }

  const sourceRevision = design.active_published_source_draft_revision;
  if (sourceRevision == null) {
    return false;
  }

  return revision !== sourceRevision;
}

export function resolveEmailCampaignDesignDisplayStatus(
  design: IEmailCampaignDesignStatus | null | undefined,
  draftRevision?: number | null,
): TEmailCampaignDesignDisplayStatus {
  if (design == null || design.active_published_design_id == null) {
    return "not_designed";
  }

  if (emailCampaignDesignHasUnpublishedChanges(design, draftRevision)) {
    return "unpublished_changes";
  }

  return "current";
}

export function emailCampaignDesignDisplayStatusLabel(
  status: TEmailCampaignDesignDisplayStatus,
): string {
  switch (status) {
    case "unpublished_changes":
      return "Unfinalized changes — finalize your design to update what recipients will see";
    case "current":
      return "Finalized design is up to date";
    case "not_designed":
    default:
      return "Design the content of your email";
  }
}

export function emailCampaignDesignStepBadgeType(
  design: IEmailCampaignDesignStatus | null | undefined,
): TEmailCampaignDesignStepBadgeType {
  const status = resolveEmailCampaignDesignDisplayStatus(design);
  if (status === "current") {
    return "success";
  }

  if (status === "unpublished_changes") {
    return "warning";
  }

  if (design?.has_draft === true) {
    return "warning";
  }

  return "gray";
}

export function emailCampaignDesignCtaLabel(
  design: IEmailCampaignDesignStatus | null | undefined,
): string {
  const status = resolveEmailCampaignDesignDisplayStatus(design);
  if (status === "unpublished_changes" || design?.has_draft === true) {
    return "Continue designing email";
  }

  if (status === "current") {
    return "Edit email design";
  }

  return "Design email";
}

export function emailCampaignDesignStepPreviewLabel(params: {
  editable: boolean;
  design: IEmailCampaignDesignStatus | null | undefined;
  status?: string | null;
}): string {
  if (!params.editable) {
    if (canUnpublishEmailCampaign(params.status)) {
      return "This email can no longer be edited while it is scheduled. Unpublish the campaign to make changes.";
    }

    return "This email can no longer be edited. Duplicate the campaign to make changes.";
  }

  if (params.design?.has_draft === true && params.design.active_published_design_id == null) {
    return "Draft saved — continue designing and finalize before you send";
  }

  return emailCampaignDesignDisplayStatusLabel(resolveEmailCampaignDesignDisplayStatus(params.design));
}

export function emailCampaignDesignBlocksFinalPublish(
  design: IEmailCampaignDesignStatus | null | undefined,
): boolean {
  if (design == null || (!design.has_draft && design.active_published_design_id == null)) {
    return false;
  }

  if (design.active_published_design_id == null) {
    return true;
  }

  return emailCampaignDesignHasUnpublishedChanges(design);
}

export function resolveEmailCampaignFinalPublishDecision(params: {
  design: IEmailCampaignDesignStatus | null | undefined;
}): TEmailCampaignFinalPublishDecision {
  if (emailCampaignDesignBlocksFinalPublish(params.design)) {
    return "published_design_required";
  }

  const hasNoBuilderDesign =
    params.design == null ||
    (!params.design.has_draft && params.design.active_published_design_id == null);

  if (hasNoBuilderDesign) {
    return "design_required";
  }

  return "allowed";
}

export type TEmailCampaignContinueDestination = "schedule" | "builder";

export function resolveEmailCampaignContinueDestination(params: {
  design: IEmailCampaignDesignStatus | null | undefined;
}): TEmailCampaignContinueDestination {
  if (resolveEmailCampaignFinalPublishDecision(params) === "allowed") {
    return "schedule";
  }

  return "builder";
}

export type TEmailCampaignDetailsScheduleReadiness = {
  audiences?: Array<{ id: number }> | null;
  from_name?: string | null;
  from_address?: string | null;
  subject?: string | null;
};

export function isEmailCampaignDetailsReadyForSchedule(
  campaign: TEmailCampaignDetailsScheduleReadiness | null | undefined,
): boolean {
  if (campaign == null) {
    return false;
  }

  if ((campaign.audiences ?? []).length < 1) {
    return false;
  }

  if ((campaign.from_name ?? "").trim().length === 0) {
    return false;
  }

  if ((campaign.from_address ?? "").trim().length === 0) {
    return false;
  }

  return (campaign.subject ?? "").trim().length > 0;
}
